import React, { useMemo } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';

import { AnimatedText } from '../../common';

const MAX_VISIBLE_STATUS_LINES = 3;

const STACK_TRANSITION = {
  opacity: { duration: 0.32, ease: 'easeOut' as const },
  y: { type: 'spring' as const, stiffness: 380, damping: 32, mass: 0.8 },
  layout: { type: 'spring' as const, stiffness: 380, damping: 32, mass: 0.8 },
};

interface SchedulerStatusMessagesProps {
  statusLines: string[];
  /** Fallback when `statusLines` is empty but a loading placeholder should show. */
  emptyFallback?: string;
  className?: string;
}

/**
 * Stacked planning status lines (plan-my-day / plan-my-week).
 * Shows up to 3 lines: newest enters from the bottom, older lines push up,
 * and the line that drops off the top fades out upward.
 */
const SchedulerStatusMessages: React.FC<SchedulerStatusMessagesProps> = ({
  statusLines,
  emptyFallback,
  className,
}) => {
  const visibleEntries = useMemo(() => {
    const lines = statusLines.length > 0 ? statusLines : emptyFallback ? [emptyFallback] : [];
    if (!lines.length) return [];

    const sliced = lines.slice(-MAX_VISIBLE_STATUS_LINES);
    const offset = lines.length - sliced.length;
    return sliced.map((text, index) => ({
      id: `${offset + index}-${index === sliced.length - 1 ? 'active' : 'past'}-${text}`,
      text,
      isActive: index === sliced.length - 1,
      sourceIndex: offset + index,
    }));
  }, [emptyFallback, statusLines]);

  if (!visibleEntries.length) {
    return null;
  }

  return (
    <div className={clsx('scheduler-chat-ui__status_messages', className)}>
      <AnimatePresence initial={false} mode="popLayout">
        {visibleEntries.map((entry) => (
          <motion.div
            key={entry.id}
            layout="position"
            className="scheduler-chat-ui__status_messages__row"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: entry.isActive ? 1 : 0.55, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={STACK_TRANSITION}
          >
            {entry.isActive ? (
              <AnimatedText
                key={`active-status-${entry.sourceIndex}-${entry.text}`}
                text={entry.text}
                className={clsx(
                  'scheduler-chat-ui__status_messages__item',
                  'scheduler-chat-ui__status_messages__item--active',
                  'scheduler-chat-ui__status_messages__item--scheduler'
                )}
              />
            ) : (
              <span className="scheduler-chat-ui__status_messages__item">{entry.text}</span>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default SchedulerStatusMessages;
